import React, { useState, useMemo } from 'react';
import { SimulationConfig, ParallaxEffectType } from './types';
import { SectionLabel } from './SectionLabel';
import { ControlGroup } from './ControlGroup';
import { Toggle } from './Toggle';
import { SmartInput } from './SmartInput';
import { EFFECT_CATEGORIES, ParallaxCategory } from './render/parallax/EffectCategories';

interface ParallaxSettingsSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
}

export const ParallaxSettingsSection: React.FC<ParallaxSettingsSectionProps> = ({ config, setConfig }) => {
  const [activeCategory, setActiveCategory] = useState<ParallaxCategory>(ParallaxCategory.CORE);
  
  const allEffects = Object.values(ParallaxEffectType).filter(e => e !== ParallaxEffectType.NONE);
  
  const filteredEffects = useMemo(() => {
    if (activeCategory === ParallaxCategory.ALL) return allEffects;
    return allEffects.filter(e => EFFECT_CATEGORIES[e as ParallaxEffectType] === activeCategory);
  }, [activeCategory, allEffects]);

  const categories = Object.values(ParallaxCategory);

  return (
    <>
      <div className="flex items-center justify-between mb-2 px-1">
        <SectionLabel icon="fa-layer-group" darkMode={config.darkMode}>Parallax Background</SectionLabel>
        <button 
          onClick={() => {
            const effectStyles = Object.values(ParallaxEffectType).filter(e => e !== 'NONE');
            const randomEffect = effectStyles[Math.floor(Math.random() * effectStyles.length)];
            setConfig(p => ({
              ...p,
              parallaxEffect: randomEffect,
              parallaxIntensity: 0.1 + Math.random() * 0.9,
              parallaxSpeed: 0.2 + Math.random() * 2,
              parallaxLayers: Math.floor(1 + Math.random() * 6),
              parallaxOpacity: 0.2 + Math.random() * 0.6,
              parallaxScale: 0.5 + Math.random() * 2,
              parallaxComplexity: 0.5 + Math.random() * 2,
              parallaxPatternDensity: 0.5 + Math.random() * 1.5,
              parallaxBlur: Math.random() > 0.7 ? Math.random() * 5 : 0,
              parallaxGlowEnabled: Math.random() > 0.5,
              parallaxColorMode: Math.random() > 0.7 ? 'RAINBOW' : (Math.random() > 0.5 ? 'CUSTOM' : 'THEME'),
              parallaxCustomColor: `#${Math.floor(Math.random()*16777215).toString(16)}`,
              parallaxMediaOpacity: 0.5 + Math.random() * 0.5
            }));
            const cat = EFFECT_CATEGORIES[randomEffect as ParallaxEffectType];
            if (cat) setActiveCategory(cat);
          }}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[9px] font-black uppercase transition-all shadow-sm active:scale-95 ${
            config.darkMode 
            ? 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 hover:bg-indigo-500/30' 
            : 'bg-indigo-100 text-indigo-600 border border-indigo-200 hover:bg-indigo-200'
          }`}
        >
          <i className="fa-solid fa-dice" />
          Randomize
        </button>
      </div>
      <div className="flex flex-col gap-3 w-full p-4 rounded-3xl border bg-neutral-500/5 border-neutral-500/10 mb-2">
        <div className="flex items-center justify-between border border-white/5 p-2 rounded-xl">
          <span className="text-[10px] font-black uppercase opacity-60">Enable Parallax</span>
          <Toggle active={config.parallaxEnabled} onClick={() => setConfig(p => ({...p, parallaxEnabled: !p.parallaxEnabled}))} color="bg-indigo-500" />
        </div>

        {config.parallaxEnabled && (
          <>
            <div className="flex flex-col gap-2">
              <span className="text-[10px] font-black uppercase opacity-60 px-1">Style Categories</span>
              <div className="flex gap-1.5 overflow-x-auto pb-2 custom-scrollbar no-scrollbar">
                {categories.map(cat => (
                  <button 
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase whitespace-nowrap transition-all border ${
                      activeCategory === cat 
                      ? 'bg-indigo-500 text-white border-indigo-400 shadow-lg shadow-indigo-500/20' 
                      : (config.darkMode ? 'bg-white/5 text-white/40 border-white/5 hover:bg-white/10' : 'bg-black/5 text-black/40 border-black/5 hover:bg-black/10')
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            <ControlGroup label={`${activeCategory} Effects`} darkMode={config.darkMode}>
              <div className="grid grid-cols-2 sm:grid-cols-2 gap-1.5 p-1.5 rounded-xl bg-black/5 border border-white/5 max-h-[200px] overflow-y-auto custom-scrollbar">
                {filteredEffects.map(effect => (
                  <button
                    key={effect}
                    onClick={() => setConfig(p => ({...p, parallaxEffect: effect}))}
                    className={`py-2 px-3 rounded-lg text-[9px] font-black uppercase transition-all text-left truncate leading-tight border ${
                      config.parallaxEffect === effect 
                      ? (config.darkMode ? 'bg-white text-black border-white' : 'bg-black text-white border-black') 
                      : 'bg-white/5 text-white/40 border-transparent hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    {effect.replace(/_/g, ' ')}
                  </button>
                ))}
              </div>
            </ControlGroup>

            {EFFECT_CATEGORIES[config.parallaxEffect as ParallaxEffectType] === ParallaxCategory.MEDIA && (
              <div className="p-3 rounded-2xl border border-purple-500/20 bg-purple-500/5 flex flex-col gap-3 mt-1">
                <span className="text-[9px] font-black uppercase text-purple-400">Media Content Configuration</span>
                
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-black uppercase opacity-60">Image URL</span>
                  <input 
                    type="text" 
                    placeholder="Enter Image URL (PNG/JPG)"
                    value={config.parallaxImageUrl} 
                    onChange={e => setConfig(p => ({...p, parallaxImageUrl: e.target.value}))}
                    className={`w-full px-3 py-2 border rounded-xl text-[10px] font-black ${config.darkMode ? 'bg-neutral-800 border-neutral-700' : 'bg-white border-neutral-200'}`}
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-black uppercase opacity-60">Video URL</span>
                  <input 
                    type="text" 
                    placeholder="Enter Video URL (MP4)"
                    value={config.parallaxVideoUrl} 
                    onChange={e => setConfig(p => ({...p, parallaxVideoUrl: e.target.value}))}
                    className={`w-full px-3 py-2 border rounded-xl text-[10px] font-black ${config.darkMode ? 'bg-neutral-800 border-neutral-700' : 'bg-white border-neutral-200'}`}
                  />
                </div>

                <div className="grid grid-cols-1 gap-2">
                   <ControlGroup label="Fitting Mode">
                      <select 
                        value={config.parallaxMediaFit} 
                        onChange={e => setConfig(p => ({...p, parallaxMediaFit: e.target.value as any}))}
                        className={`w-full px-2 py-1 border rounded-lg text-[10px] font-black ${config.darkMode ? 'bg-neutral-800 border-neutral-700' : 'bg-white border-neutral-200'}`}
                      >
                        <option value="COVER">Cover (Fill)</option>
                        <option value="CONTAIN">Contain (Best Fit)</option>
                        <option value="STRETCH">Stretch</option>
                        <option value="TILE">Tile (Repeat)</option>
                      </select>
                   </ControlGroup>
                   <SmartInput label="Media Opacity" min={0} max={1} step={0.05} value={config.parallaxMediaOpacity} onChange={v => setConfig(p => ({...p, parallaxMediaOpacity: v}))} darkMode={config.darkMode} />
                </div>
              </div>
            )}

            {config.parallaxEffect === ParallaxEffectType.WATERMARK && (
              <div className="p-3 rounded-2xl border border-blue-500/20 bg-blue-500/5 flex flex-col gap-3 mt-2">
                <span className="text-[9px] font-black uppercase text-blue-400">Watermark Configuration</span>
                
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-black uppercase opacity-60">Watermark Text</span>
                  <input 
                    type="text" 
                    value={config.parallaxWatermarkText} 
                    onChange={e => setConfig(p => ({...p, parallaxWatermarkText: e.target.value}))}
                    className={`w-full px-3 py-2 border rounded-xl text-[10px] font-black ${config.darkMode ? 'bg-neutral-800 border-neutral-700' : 'bg-white border-neutral-200'}`}
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <ControlGroup label="Font Family">
                    <select 
                      value={config.parallaxWatermarkFontFamily} 
                      onChange={e => setConfig(p => ({...p, parallaxWatermarkFontFamily: e.target.value}))}
                      className={`w-full px-2 py-1 border rounded-lg text-[10px] font-black ${config.darkMode ? 'bg-neutral-800 border-neutral-700' : 'bg-white border-neutral-200'}`}
                    >
                      <option value="Inter">Inter</option>
                      <option value="Space Grotesk">Space Grotesk</option>
                      <option value="JetBrains Mono">JetBrains Mono</option>
                      <option value="Outfit">Outfit</option>
                      <option value="Playfair Display">Playfair Display</option>
                    </select>
                  </ControlGroup>
                  <ControlGroup label="Font Weight">
                    <select 
                      value={config.parallaxWatermarkFontWeight} 
                      onChange={e => setConfig(p => ({...p, parallaxWatermarkFontWeight: e.target.value}))}
                      className={`w-full px-2 py-1 border rounded-lg text-[10px] font-black ${config.darkMode ? 'bg-neutral-800 border-neutral-700' : 'bg-white border-neutral-200'}`}
                    >
                      <option value="100">Thin</option>
                      <option value="400">Normal</option>
                      <option value="700">Bold</option>
                      <option value="900">Black</option>
                    </select>
                  </ControlGroup>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <ControlGroup label="Color">
                    <input type="color" value={config.parallaxWatermarkColor} onChange={e => setConfig(p => ({...p, parallaxWatermarkColor: e.target.value}))} className="w-full h-8 rounded-lg cursor-pointer bg-transparent" />
                  </ControlGroup>
                  <div className="flex items-center justify-between border border-white/5 p-2 rounded-xl">
                    <span className="text-[8px] font-black uppercase opacity-60">Use Ball Color</span>
                    <Toggle active={config.parallaxWatermarkUseBallColor} onClick={() => setConfig(p => ({...p, parallaxWatermarkUseBallColor: !p.parallaxWatermarkUseBallColor}))} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <SmartInput label="Opacity" min={0} max={1} step={0.05} value={config.parallaxWatermarkOpacity} onChange={v => setConfig(p => ({...p, parallaxWatermarkOpacity: v}))} darkMode={config.darkMode} />
                  <SmartInput label="Font Size" min={10} max={200} value={config.parallaxWatermarkFontSize} onChange={v => setConfig(p => ({...p, parallaxWatermarkFontSize: v}))} darkMode={config.darkMode} />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <SmartInput label="Density" min={0.1} max={2} step={0.1} value={config.parallaxWatermarkDensity} onChange={v => setConfig(p => ({...p, parallaxWatermarkDensity: v}))} darkMode={config.darkMode} />
                  <SmartInput label="Speed" min={0} max={10} step={0.1} value={config.parallaxWatermarkSpeed} onChange={v => setConfig(p => ({...p, parallaxWatermarkSpeed: v}))} darkMode={config.darkMode} />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <SmartInput label="Repeat X" min={1} max={50} value={config.parallaxWatermarkRepeatX} onChange={v => setConfig(p => ({...p, parallaxWatermarkRepeatX: v}))} darkMode={config.darkMode} />
                  <SmartInput label="Repeat Y" min={1} max={50} value={config.parallaxWatermarkRepeatY} onChange={v => setConfig(p => ({...p, parallaxWatermarkRepeatY: v}))} darkMode={config.darkMode} />
                </div>

                <SmartInput label="Angle" min={-180} max={180} value={config.parallaxWatermarkAngle} onChange={v => setConfig(p => ({...p, parallaxWatermarkAngle: v}))} darkMode={config.darkMode} />

                <div className="h-px bg-white/5 my-1" />
                <span className="text-[9px] font-black uppercase text-blue-400/60">Shadow Controls</span>

                <div className="grid grid-cols-2 gap-2">
                  <ControlGroup label="Shadow Color">
                    <input type="color" value={config.parallaxWatermarkShadowColor} onChange={e => setConfig(p => ({...p, parallaxWatermarkShadowColor: e.target.value}))} className="w-full h-8 rounded-lg cursor-pointer bg-transparent" />
                  </ControlGroup>
                  <SmartInput label="Shadow Blur" min={0} max={50} value={config.parallaxWatermarkShadowBlur} onChange={v => setConfig(p => ({...p, parallaxWatermarkShadowBlur: v}))} darkMode={config.darkMode} />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <SmartInput label="Shadow X" min={-50} max={50} value={config.parallaxWatermarkShadowOffsetX} onChange={v => setConfig(p => ({...p, parallaxWatermarkShadowOffsetX: v}))} darkMode={config.darkMode} />
                  <SmartInput label="Shadow Y" min={-50} max={50} value={config.parallaxWatermarkShadowOffsetY} onChange={v => setConfig(p => ({...p, parallaxWatermarkShadowOffsetY: v}))} darkMode={config.darkMode} />
                </div>
              </div>
            )}


            <div className="flex flex-col gap-3 mt-2">
                <SmartInput label="Intensity" min={0} max={1} step={0.01} value={config.parallaxIntensity} onChange={v => setConfig(p => ({...p, parallaxIntensity: v}))} darkMode={config.darkMode} />
                <SmartInput label="Speed" min={0} max={5} step={0.1} value={config.parallaxSpeed} onChange={v => setConfig(p => ({...p, parallaxSpeed: v}))} darkMode={config.darkMode} />
                <SmartInput label="Layers" min={1} max={10} step={1} value={config.parallaxLayers} onChange={v => setConfig(p => ({...p, parallaxLayers: v}))} darkMode={config.darkMode} />
                <SmartInput label="Opacity" min={0} max={1} step={0.01} value={config.parallaxOpacity} onChange={v => setConfig(p => ({...p, parallaxOpacity: v}))} darkMode={config.darkMode} />
            </div>

            <div className="p-3 rounded-2xl border border-white/5 bg-white/5 flex flex-col gap-3 mt-1">
                <span className="text-[9px] font-black uppercase opacity-60">Advanced Settings</span>
                
                <div className="flex flex-col gap-3">
                    <SmartInput label="Blur" min={0} max={20} step={0.5} value={config.parallaxBlur} onChange={v => setConfig(p => ({...p, parallaxBlur: v}))} darkMode={config.darkMode} />
                    <SmartInput label="Scale" min={0.1} max={5} step={0.1} value={config.parallaxScale} onChange={v => setConfig(p => ({...p, parallaxScale: v}))} darkMode={config.darkMode} />
                    <SmartInput label="Complexity" min={0.1} max={5} step={0.1} value={config.parallaxComplexity} onChange={v => setConfig(p => ({...p, parallaxComplexity: v}))} darkMode={config.darkMode} />
                    <SmartInput label="Rotation" min={-180} max={180} value={config.parallaxRotation} onChange={v => setConfig(p => ({...p, parallaxRotation: v}))} darkMode={config.darkMode} />
                </div>

                <div className="flex flex-col gap-3">
                    <SmartInput label="Density" min={0.1} max={3} step={0.1} value={config.parallaxPatternDensity} onChange={v => setConfig(p => ({...p, parallaxPatternDensity: v}))} darkMode={config.darkMode} />
                    <SmartInput label="Zoom Inf." min={0} max={1} step={0.01} value={config.parallaxZoomInfluence} onChange={v => setConfig(p => ({...p, parallaxZoomInfluence: v}))} darkMode={config.darkMode} />
                </div>

                <div className="h-px bg-white/5 my-1" />
                <span className="text-[8px] font-black uppercase opacity-40">Color & Post Processing</span>
                
                <div className="flex flex-col gap-3">
                    <SmartInput label="Bright" min={0} max={2} step={0.05} value={config.parallaxBrightness} onChange={v => setConfig(p => ({...p, parallaxBrightness: v}))} darkMode={config.darkMode} />
                    <SmartInput label="Contr." min={0} max={2} step={0.05} value={config.parallaxContrast} onChange={v => setConfig(p => ({...p, parallaxContrast: v}))} darkMode={config.darkMode} />
                    <SmartInput label="Satur." min={0} max={2} step={0.05} value={config.parallaxSaturation} onChange={v => setConfig(p => ({...p, parallaxSaturation: v}))} darkMode={config.darkMode} />
                </div>

                <div className="h-px bg-white/5 my-1" />
                <span className="text-[8px] font-black uppercase opacity-40">Auto Scrolling</span>
                <div className="flex flex-col gap-3">
                    <SmartInput label="Auto X" min={-2} max={2} step={0.1} value={config.parallaxAutoScrollX} onChange={v => setConfig(p => ({...p, parallaxAutoScrollX: v}))} darkMode={config.darkMode} />
                    <SmartInput label="Auto Y" min={-2} max={2} step={0.1} value={config.parallaxAutoScrollY} onChange={v => setConfig(p => ({...p, parallaxAutoScrollY: v}))} darkMode={config.darkMode} />
                </div>

                <div className="h-px bg-white/5 my-1" />
                <div className="flex items-center justify-between">
                    <span className="text-[8px] font-black uppercase opacity-40">Glow Effect</span>
                    <Toggle active={config.parallaxGlowEnabled} onClick={() => setConfig(p => ({...p, parallaxGlowEnabled: !p.parallaxGlowEnabled}))} />
                </div>
                {config.parallaxGlowEnabled && (
                    <div className="flex flex-col gap-3 pl-2 border-l border-white/10">
                        <SmartInput label="Glow Rad." min={0} max={100} value={config.parallaxGlowRadius} onChange={v => setConfig(p => ({...p, parallaxGlowRadius: v}))} darkMode={config.darkMode} />
                        <SmartInput label="Glow Opac." min={0} max={1} step={0.05} value={config.parallaxGlowOpacity} onChange={v => setConfig(p => ({...p, parallaxGlowOpacity: v}))} darkMode={config.darkMode} />
                    </div>
                )}
            </div>

            <ControlGroup label="Color Mode">
              <select 
                value={config.parallaxColorMode} 
                onChange={e => setConfig(p => ({...p, parallaxColorMode: e.target.value as any}))}
                className={`w-full px-3 py-1.5 border rounded-xl text-xs font-bold outline-none ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}
              >
                <option value="THEME">Theme Adaptive</option>
                <option value="CUSTOM">Custom Color</option>
                <option value="RAINBOW">Rainbow Cycle</option>
              </select>
            </ControlGroup>

            {config.parallaxColorMode === 'CUSTOM' && (
              <ControlGroup label="Custom Color">
                <input type="color" value={config.parallaxCustomColor} onChange={e => setConfig(p => ({...p, parallaxCustomColor: e.target.value}))} className="w-full h-8 rounded-lg cursor-pointer bg-transparent" />
              </ControlGroup>
            )}
          </>
        )}
      </div>
    </>
  );
};
